import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { RouteModalTypes } from '@lp/features/RoutePlanner/types';
import { useRouteModalActions } from '@lp/features/RoutePlanner/hooks/useRouteModals';
import { useGetQuoteDetails } from '@lp/features/RoutePlanner/hooks/useGetQuoteDetails';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import {
  useQuoteApproveInternally,
  useQuoteApproveTxCenterProvider,
  useQuoteRecall,
  useQuoteRejectInternally,
} from '@/queries/quotes';
import { QuoteApproveInternallyBody } from '@/api/apis/quote';
import { retrieveUnifiedError } from '@/api/utils';

export const useQuoteDetails = () => {
  const { caseId, quote } = useCaseDetailsWithReviews();
  const { onOpenModal, onCloseModal } = useRouteModalActions();
  const quoteDetails = useGetQuoteDetails({ quote });
  const { mutateAsync: approveQuoteInternally } = useQuoteApproveInternally();
  const { mutateAsync: rejectQuoteInternally } = useQuoteRejectInternally();
  const { mutateAsync: approveTxCenterProvider } = useQuoteApproveTxCenterProvider();
  const { mutateAsync: mutateRecallQuote } = useQuoteRecall();

  const handleApproveQuoteInternally = useCallback(
    async (data: QuoteApproveInternallyBody & { id: string }) => {
      try {
        await approveQuoteInternally({
          caseId,
          id: data.id,
          groundQuotePriceInCents: data.groundQuotePriceInCents,
          airQuotePriceInCents: data.airQuotePriceInCents,
          comment: data.comment,
        });
        toast.success('Success!');
        onCloseModal();
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);
      }
    },
    [caseId, approveQuoteInternally, onCloseModal],
  );

  const handleRejectQuoteInternally = useCallback(
    async (id: string, comment?: string) => {
      try {
        await rejectQuoteInternally({ caseId, id, comment });
        toast.success('Success!');
        onCloseModal();
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);
      }
    },
    [caseId, onCloseModal, rejectQuoteInternally],
  );

  const handleApproveTxCenterProvider = useCallback(
    async (id: string, comment?: string) => {
      try {
        await approveTxCenterProvider({ id, comment });
        toast.success('Success!');
        onCloseModal();
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);
      }
    },
    [onCloseModal, approveTxCenterProvider],
  );

  const handleRecallQuote = useCallback(
    async (id: string, comment: string) => {
      try {
        await mutateRecallQuote({ id, comment });
        toast.success('Success!');
        onCloseModal();
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);
      }
    },
    [onCloseModal, mutateRecallQuote],
  );

  const openQuote = useCallback(
    ({
      rejectedByTxCenter,
      isViewOnly,
      recallQuote,
      desyncedState,
    }: {
      rejectedByTxCenter?: boolean;
      isViewOnly?: boolean;
      recallQuote?: boolean;
      desyncedState?: boolean;
    }) => {
      onOpenModal({
        type: RouteModalTypes.QuoteDetails,
        context: {
          isViewOnly,
          recallQuote,
          desyncedState,
          onApprove: handleApproveQuoteInternally,
          onReject: handleRejectQuoteInternally,
          onRecall: handleRecallQuote,
          onApproveTxCenterProvider: rejectedByTxCenter ? handleApproveTxCenterProvider : undefined,
        },
      });
    },
    [
      handleRecallQuote,
      handleApproveQuoteInternally,
      handleRejectQuoteInternally,
      onOpenModal,
      handleApproveTxCenterProvider,
    ],
  );

  return {
    handleApproveQuoteInternally,
    handleRejectQuoteInternally,
    openQuote,
    quoteDetails,
  };
};
